import React, { useState, useEffect } from 'react';
import { dynastyTheme } from '../../../../../services/colorService';
import PropTypes from 'prop-types';
import { mlbAPI } from '../../../../../services/apiService';
import { mobileColors, mobileTextStyles } from '../../../../../services/mobileStyleService';

const C = dynastyTheme.tokens.colors;
const GOLD = dynastyTheme.tokens.colors.primary;
const ROW_HAIRLINE = `1px solid ${C.hairline}`;

/**
 * NewsTabContent — player news via mlbAPI.getPlayerNotes(playerId) (backend
 * filters by player_id). FLAT house treatment (no package mockup): no rounded
 * card, no border-radius, no per-item colored left accent — flat rows separated
 * by a thin hairline, each headline (800) then body then a muted source · time
 * line. 4px boundary is provided by the modal body wrapper.
 */
const NewsTabContent = ({ playerId }) => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!playerId) { setLoading(false); return; }
    mlbAPI.getPlayerNotes(playerId)
      .then(notes => {
        setNews(Array.isArray(notes) ? notes : []);
      })
      .catch(() => setNews([]))
      .finally(() => setLoading(false));
  }, [playerId]);

  if (loading) return <div style={{ padding: 30, textAlign: 'center', color: C.coolGray[550], fontSize: 11 }}>Loading news...</div>;

  if (!news.length) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: 12, color: C.coolGray[550], marginBottom: 4 }}>No recent news available</div>
        <div style={{ fontSize: 10, color: C.coolGray[720] }}>Check back later for updates</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '8px 2px' }}>
      <div style={{ color: GOLD, ...mobileTextStyles.sectionHeader, marginBottom: 4 }}>Recent News</div>
      {news.map((item, i) => {
        const headline = item.headline || item.content || '';
        const body = (item.headline && item.content && item.content !== item.headline) ? item.content : null;
        const meta = [item.source, formatTimeAgo(item.date)].filter(Boolean).join(' · ');
        return (
          <div key={item.note_id || i} style={{ padding: '9px 2px', borderBottom: i < news.length - 1 ? ROW_HAIRLINE : 'none' }}>
            <div style={{ fontSize: 12, fontWeight: 400, color: mobileColors.textSecondary, lineHeight: 1.35 }}>{headline}</div>
            {body && <div style={{ fontSize: 11, fontWeight: 400, color: mobileColors.textTertiary, lineHeight: 1.4, marginTop: 3 }}>{body}</div>}
            {meta && <div style={{ fontSize: 10, color: mobileColors.textMuted, marginTop: 3 }}>{meta}</div>}
          </div>
        );
      })}
    </div>
  );
};

const formatTimeAgo = (dateString) => {
  if (!dateString) return 'Recently';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return `${Math.floor(diffDays / 30)}mo ago`;
};

NewsTabContent.propTypes = {
  playerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default NewsTabContent;
